import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, rgbaColor, numberFormatter, formatCurrency } from '../../../helpers/utils';
import SnapshotService from '../services';
import CardEmptyResult from 'components/common/pageComponents/CardEmptyResult';
import { PiInfoDuotone } from 'react-icons/pi';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

const MonthlySummaryChart = ({ limit = 12, year }) => {
  const [currency_symbol, setCurrencySymbol] = useState('$');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const getData = useCallback(() => {
    setIsLoading(true);
    const params = {
      limit: limit
    };
    
    if (year) params.year = year;

    SnapshotService.getMonthlySummaries(params).then((response) => {
      try {
        if (response.data?.success && response.data?.data) {
          const { data: chartData, currency } = response.data.data;
          const list = Array.isArray(chartData) ? chartData : [];
          // Sort by year_month ascending for proper chronological order
          const sortedData = [...list].sort((a, b) => (a.year_month || '').localeCompare(b.year_month || ''));
          setData(sortedData);
          setCurrencySymbol(currency?.currency_symbol || '$');
        }
      } catch (error) {
        console.error('Error fetching monthly summary data:', error);
      } finally {
        setIsLoading(false);
      }
    }).catch(error => {
      console.error('Error fetching monthly summary data:', error);
      setIsLoading(false);
    });
  }, [limit, year]);

  useEffect(() => {
    getData();
  }, [getData]);

  const labels = useMemo(() => data.map(item => {
    const [y, m] = item.year_month.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(m) - 1]} ${y}`;
  }), [data]);

  const salesAmounts = useMemo(() => data.map(item => parseFloat(item.total_sales_amount) || 0), [data]);
  const paymentsAmounts = useMemo(() => data.map(item => parseFloat(item.total_payments_amount) || 0), [data]);
  const outstandingAmounts = useMemo(() => data.map(item => parseFloat(item.total_outstanding_amount) || 0), [data]);

  const tooltipFormatter = useCallback((params) => {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const firstParam = params[0];
    const periodLabel = firstParam.name || '';
    const index = firstParam.dataIndex;
    const summary = data[index];
    
    if (!summary) return '';

    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${periodLabel}</div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('primary')}; margin-right: 6px;"></span>
          Sales: ${formatCurrency(summary.total_sales_amount, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('success')}; margin-right: 6px;"></span>
          Payments: ${formatCurrency(summary.total_payments_amount, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('warning')}; margin-right: 6px;"></span>
          Outstanding: ${formatCurrency(summary.total_outstanding_amount, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          Sales Count: ${summary.total_sales_count || 0}
        </div>
      </div>
    `;
  }, [currency_symbol, data]);

  const getOptions = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter,
      backgroundColor: rgbaColor(getColor('gray-800'), 0.95),
      borderColor: getColor('gray-600'),
      textStyle: { color: getColor('gray-100') }
    },
    xAxis: {
      type: 'category',
      data: labels,
      boundaryGap: false,
      axisPointer: { type: 'shadow' },
      axisLabel: {
        color: getColor('gray-400'),
        rotate: labels.length > 6 ? 45 : 0
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisPointer: { show: false },
      splitLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      axisLabel: {
        show: true,
        color: getColor('gray-400'),
        formatter: (value) => {
          return `${currency_symbol} ${numberFormatter(value, 0)}`
        },
        margin: 15
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        name: 'Sales',
        type: 'line',
        data: salesAmounts,
        lineStyle: { color: getColor('primary') },
        itemStyle: {
          borderColor: getColor('primary'),
          borderWidth: 1
        },
        symbol: 'circle',
        symbolSize: 4,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(getColor('primary'), 0.2) },
              { offset: 1, color: rgbaColor(getColor('primary'), 0) }
            ]
          }
        }
      },
      {
        name: 'Payments',
        type: 'line',
        data: paymentsAmounts,
        lineStyle: { color: getColor('success') },
        itemStyle: {
          borderColor: getColor('success'),
          borderWidth: 1
        },
        symbol: 'circle',
        symbolSize: 4,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(getColor('success'), 0.2) },
              { offset: 1, color: rgbaColor(getColor('success'), 0) }
            ]
          }
        }
      },
      {
        name: 'Outstanding',
        type: 'line',
        data: outstandingAmounts,
        lineStyle: { color: getColor('warning') },
        itemStyle: {
          borderColor: getColor('warning'),
          borderWidth: 1
        },
        symbol: 'circle',
        symbolSize: 4,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(getColor('warning'), 0.2) },
              { offset: 1, color: rgbaColor(getColor('warning'), 0) }
            ]
          }
        }
      }
    ],
    legend: {
      data: ['Sales', 'Payments', 'Outstanding'],
      bottom: 0,
      textStyle: {
        color: getColor('gray-400')
      }
    },
    grid: { right: 10, left: 0, bottom: 30, top: 10, containLabel: true }
  }), [labels, currency_symbol, tooltipFormatter, salesAmounts, paymentsAmounts, outstandingAmounts]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  const hasData =
    data.length > 0 &&
    (salesAmounts.some(value => value > 0) ||
      paymentsAmounts.some(value => value > 0) ||
      outstandingAmounts.some(value => value > 0));

  if (!hasData) {
    return (
      <CardEmptyResult
        title="No monthly summary data"
        subtitle="There is no monthly summary data available for the selected criteria."
        icon={<PiInfoDuotone className="fs-2 text-secondary" />}
      />
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getOptions}
      style={{ height: '10.4375rem' }}
    />
  );
};

export default MonthlySummaryChart;

